/**
 * Task 2 normal-completion navigation policy.
 *
 * Explicit Quit uses markLiveExamRuntimeExiting() and must never be reused here.
 * Persistence (voice-rtc-7 task-entry) is non-blocking and must not gate routing.
 */

import type { Task2EndingSource } from './task2EndingAuthority';

export type Task2CompletionDestination = '/task-three' | '/task2-processing';

/** Golden-live: skip duplicate onTaskCompleted when another ending is in progress. */
export function shouldSkipTask2OnTaskCompleted(params: {
  claimant: Task2EndingSource;
  agentReportedPlayedAudio: boolean;
  endingAlreadyStartedRef: boolean;
}): boolean {
  if (params.agentReportedPlayedAudio && params.claimant === 'tool') {
    return false;
  }
  if (params.claimant !== 'idle') {
    return true;
  }
  if (params.endingAlreadyStartedRef && !params.agentReportedPlayedAudio) {
    return true;
  }
  return false;
}

/** Defer agent tool navigation while page-owned static ending is active. */
export function shouldDeferAgentTask2Navigate(claimant: Task2EndingSource): boolean {
  return claimant === 'cta' || claimant === 'timer' || claimant === 'onTaskCompleted';
}

export type Task2TimerExpiryPlan =
  | 'play_ending_and_navigate'
  | 'defer_authoritative_ending'
  | 'silent_abort';

export function getTask2CompletionDestination(isRetryAttempt: boolean): Task2CompletionDestination {
  return isRetryAttempt ? '/task2-processing' : '/task-three';
}

export function planTask2TimerExpiry(params: {
  runtimeExiting: boolean;
  canClaimTimerEnding: boolean;
}): Task2TimerExpiryPlan {
  if (params.runtimeExiting) return 'silent_abort';
  if (params.canClaimTimerEnding) return 'play_ending_and_navigate';
  return 'defer_authoritative_ending';
}

export function shouldCommitTask2CompletionNavigation(params: {
  runtimeExiting: boolean;
  alreadyNavigated: boolean;
}): boolean {
  return !params.runtimeExiting && !params.alreadyNavigated;
}
